<style scoped>
.doc-link,
.doc-link:visited,
.doc-link:hover,
.doc-link:focus {
  color: #000 !important;
  text-decoration: underline;
}
</style>
<template>
  <b-overlay :show="loading" v-if="transactions">
    <b-row>
      <b-col class="text-left">
        <h2>{{ heading }}</h2>
      </b-col>
    </b-row>
    <b-row v-if="selectedCount > 0" class="mb-2">
      <b-col class="d-flex align-items-center">
        <div class="mr-3">{{ selectedCount }} selected</div>
        <div class="text-muted small mr-2">
          {{ canAsk ? 'Each selected must have a Question (or files)' : 'Each selected must have an Answer (or files)' }}
        </div>
        <b-button
          size="sm"
          variant="success"
          class="mr-2"
          :disabled="!canBulkSend || bulkSending"
          @click="sendAll()"
        >
          <b-spinner small class="mr-1" v-if="bulkSending" /> Send All
        </b-button>
        <b-button size="sm" variant="outline-secondary" :disabled="bulkSending" @click="clearSelection">
          Clear
        </b-button>
      </b-col>
    </b-row>
    <b-table
      :id="tableName"
      :fields="headers"
      :items="transactions"
      :sort-by.sync="sort.column"
      :sort-desc.sync="sort.reverse"
      :sort-compare="sortCompare"
      bordered
      sticky-header="600px"
      class="small"
      show-empty
      primary-key="id"
      tbody-tr-class="align-middle"
      tfoot-tr-class="align-middle"
      striped
      @sort-changed="(ctx) => $emit('sort-changed', ctx)"
    >
      <template #head(_select)>
        <b-form-checkbox
          :indeterminate="isIndeterminate"
          :checked="allOnPageSelected"
          @change="toggleSelectAll($event)"
          aria-label="Select all"
        />
      </template>

      <template #cell(_select)="data">
        <b-form-checkbox
          :checked="isSelected(data.item.id)"
          @change="toggleSelect(data.item.id, $event)"
          aria-label="Select row"
        />
      </template>

      <template #cell(qbtype)="data">
        {{ data.item.qbtype.replace(/([A-Z])/g, ' $1') }}
      </template>

      <template #cell(amount)="data">
        <strong>{{ data.item.amount | currency }}</strong>
      </template>

      <template #cell(statusRank)="data">
        <span>{{ getStatus(data.item) }}</span>
      </template>
      <template #cell(question)="data">
        <div>
          <div v-if="senderStamp(data.item,'question')" class="text-muted small mb-1">
            {{ senderStamp(data.item,'question') }}
          </div>
          <b-form-textarea
            v-if="canAsk"
            :value="draftText(data.item,'question')"
            @input="setDraft(data.item.id,'question',$event)"
            rows="3"
            max-rows="6"
            :placeholder="placeholderFor(data.item)"
            :disabled="sendingMap[getKey(data.item.id,'question')]"
            class="w-100"
            :state="fieldState(data.item.id, 'question')"
          />
          <div v-else class="text-muted" style="white-space: pre-wrap;">
            {{ draftText(data.item, 'question') || '—' }}
          </div>
          <div v-if="docsForSide(data.item,'question').length" class="mt-2">
            <ul class="mb-2 pl-3 small">
              <li v-for="d in docsForSide(data.item,'question')" :key="`qdoc-${d.id}`">
                <a
                  :href="d.url"
                  :download="d.documentname"
                  target="_blank"
                  rel="noopener"
                  class="doc-link"
                >{{ d.documentname }}</a>
              </li>
            </ul>
          </div>

          <div class="mt-1">
            <div class="d-flex align-items-center justify-content-end">
              <input
                type="file"
                :ref="fileRefId(data.item.id,'question')"
                class="d-none"
                multiple
                @change="onNativeFilesPicked(data.item.id,'question',$event)"
              />
              <b-button
                v-if="canAsk"
                size="sm"
                variant="outline-secondary"
                class="mr-2"
                v-b-tooltip.hover
                title="Add attachments"
                @click="triggerFilePicker(data.item.id,'question')"
              >
                <b-icon-paperclip />
              </b-button>

              <b-button
                size="sm"
                variant="success"
                class="mr-2"
                v-if="canAsk"
                :disabled="(!hasDraft(data.item.id,'question') && !hasFiles(data.item.id,'question')) || sendingMap[getKey(data.item.id,'question')]"
                @click="send(data.item,'question')"
              >
                <b-spinner small class="mr-1" v-if="sendingMap[getKey(data.item.id,'question')]" /> Send
              </b-button>

              <!-- <b-button
                size="sm"
                variant="outline-secondary"
                class="mr-2"
                v-if="canAsk"
                :disabled="(!hasDraft(data.item.id,'question') && !hasFiles(data.item.id,'question')) || sendingMap[getKey(data.item.id,'question')]"
                @click="resetDraft(data.item.id,'question'); resetFiles(data.item.id,'question')"
              >
                Reset
              </b-button> -->

              <!-- <b-button
                variant="link"
                class="p-0"
                :disabled="!lastMsgByRole(data.item,['Bookkeeper','Administrator'])"
                v-b-tooltip.hover
                title="View last bookkeeper message"
                @click.stop="openMessageView(lastMsgByRole(data.item,['Bookkeeper','Administrator']))"
              >
                <b-icon-info-circle-fill />
              </b-button> -->
            </div>

            <ul v-if="hasFiles(data.item.id,'question')" class="mt-2 mb-0 pl-3 small text-muted">
              <li
                v-for="(f, i) in filesOf(data.item.id,'question')"
                :key="`qf-${data.item.id}-${i}`"
              >
                {{ f.name }}
              </li>
            </ul>
          </div>
        </div>
      </template>

      <template #cell(answer)="data">
        <div>
          <div v-if="senderStamp(data.item,'answer')" class="text-muted small mb-1">
            {{ senderStamp(data.item,'answer') }}
          </div>
          <b-form-textarea
            v-if="canAnswer"
            :value="draftText(data.item,'answer')"
            @input="setDraft(data.item.id,'answer',$event)"
            rows="3"
            max-rows="6"
            placeholder="Reply to bookkeeper…"
            :disabled="sendingMap[getKey(data.item.id,'answer')]"
            class="w-100"
            :state="fieldState(data.item.id, 'answer')"
          />
          <div v-else class="text-muted" style="white-space: pre-wrap;">
            {{ draftText(data.item, 'answer') || '—' }}
          </div>
          <div v-if="docsForSide(data.item,'answer').length" class="mt-2">
            <ul class="mb-2 pl-3 small">
              <li v-for="d in docsForSide(data.item,'answer')" :key="`adoc-${d.id}`">
                <a
                  :href="d.url"
                  :download="d.documentname"
                  target="_blank"
                  rel="noopener"
                  class="doc-link"
                >{{ d.documentname }}</a>
              </li>
            </ul>
          </div>

          <div class="mt-1">
            <div class="d-flex align-items-center justify-content-end">
              <input
                type="file"
                :ref="fileRefId(data.item.id,'answer')"
                class="d-none"
                multiple
                @change="onNativeFilesPicked(data.item.id,'answer',$event)"
              />
              <b-button
                v-if="canAnswer"
                size="sm"
                variant="outline-secondary"
                class="mr-2"
                v-b-tooltip.hover
                title="Add attachments"
                @click="triggerFilePicker(data.item.id,'answer')"
              >
                <b-icon-paperclip />
              </b-button>

              <b-button
                size="sm"
                variant="success"
                class="mr-2"
                v-if="canAnswer"
                :disabled="(!hasDraft(data.item.id,'answer') && !hasFiles(data.item.id,'answer')) || sendingMap[getKey(data.item.id,'answer')]"
                @click="send(data.item,'answer')"
              >
                <b-spinner small class="mr-1" v-if="sendingMap[getKey(data.item.id,'answer')]" /> Send
              </b-button>

              <!-- <b-button
                size="sm"
                variant="outline-secondary"
                class="mr-2"
                v-if="canAnswer"
                :disabled="(!hasDraft(data.item.id,'answer') && !hasFiles(data.item.id,'answer')) || sendingMap[getKey(data.item.id,'answer')]"
                @click="resetDraft(data.item.id,'answer'); resetFiles(data.item.id,'answer')"
              >
                Reset
              </b-button> -->

              <!-- <b-button
                variant="link"
                class="p-0"
                :disabled="!lastMsgByRole(data.item,['Client'])"
                v-b-tooltip.hover
                title="View last client message"
                @click.stop="openMessageView(lastMsgByRole(data.item,['Client']))"
              >
                <b-icon-info-circle-fill />
              </b-button> -->
            </div>

            <ul v-if="hasFiles(data.item.id,'answer')" class="mt-2 mb-0 pl-3 small text-muted">
              <li
                v-for="(f, i) in filesOf(data.item.id,'answer')"
                :key="`af-${data.item.id}-${i}`"
              >
                {{ f.name }}
              </li>
            </ul>
          </div>
        </div>
      </template>

      <template #cell(action)="data" v-if="canAsk">
        <b-button
          v-if="showAction(data.item)"
          size="sm"
          variant="success"
          :disabled="markingMap[data.item.id]"
          @click="markComplete(data.item)"
        >
          <b-spinner small class="mr-1" v-if="markingMap[data.item.id]" /> Mark Complete
        </b-button>
      </template>

      <template
        #custom-foot="data"
        v-if="total > 0"
      >
        <b-tr class="align-middle">
          <b-td :colspan="+data.columns">
            <TransactionPagination
              class="m-0"
              :current-page="currentPage"
              :per-page="perPage"
              :total-records="total"
              :table-name="tableName"
              @update-page="(v) => { $emit('update-page', v) }"
              @update-per-page="(v) => { $emit('update-per-page', v) }"
            />
          </b-td>
        </b-tr>
      </template>
    </b-table>

    <!-- <b-modal
      id="inline-msg-view"
      v-model="showMessageDetail"
      size="lg"
      v-if="selectedMessage"
      :title="'Message ID ' + selectedMessage.id"
      hide-footer
      no-close-on-backdrop
      no-close-on-esc
      button-size="sm"
      @close="selectedMessage = null"
    >
      <MessageView
        :message="selectedMessage"
        @update="handleMessageUpdated"
        @close="$root.$emit('bv::hide::modal','inline-msg-view')"
      />
    </b-modal> -->
    <!-- <b-modal
      id="tx-detail-modal"
      v-model="showTransactionDetail"
      size="lg"
      v-if="selectedTransaction"
      :title="'Transaction ID ' + selectedTransaction.id"
      no-close-on-backdrop
      no-close-on-esc
      @close="selectedTransaction = null"
    >
      <TransactionDetail :transaction="selectedTransaction" />
    </b-modal> -->
  </b-overlay>
</template>

<script>
import TransactionPagination from '@/components/bookkeeping/widgets/TransactionPagination'
// import MessageView from '@/components/feedback/MessageView'
import MessageService from '@/services/MessageService'
import FeedbackService from '@/services/FeedbackService'
// import TransactionDetail from '@/components/bookkeeping/widgets/TransactionDetail'

export default {
  name: 'TransactionList',
  components: { TransactionPagination },
  props: {
    transactions: { type: Array, default: () => [] },
    status: { type: Object, default: () => null },
    amount: { type: Number, default: 0 },
    total: { type: Number, default: 0 },
    currentPage: Number,
    perPage: Number,
    tableName: String,
    heading: String,
    sort: { type: Object, default: () => null },
    role: { type: String, default: 'user' }
  },
  computed: {
    canAsk () {
      return this.role === 'bookkeeper' || this.role === 'admin'
    },
    canAnswer () {
      return this.role === 'user'
    },
    headers () {
      const cols = [...this.baseHeaders]
      if (this.canAsk) {
        cols.push({ key: 'action', label: 'Actions', tdClass: 'align-middle text-center', thClass: 'text-center' })
      }
      return cols
    },
    selectedCount () { return this.selectedIds.size },
    selectedOnPage () {
      const onPage = new Set((this.transactions || []).map(t => +t.id))
      return [...this.selectedIds].filter(id => onPage.has(+id))
    },
    allOnPageSelected () {
      const rows = this.transactions || []
      if (!rows.length) return false
      return rows.every(r => this.selectedIds.has(+r.id))
    },
    isIndeterminate () {
      return this.selectedOnPage.length > 0 && !this.allOnPageSelected
    },
    requireSide () { return this.canAsk ? 'question' : 'answer' },
    canBulkSend () {
      if (this.selectedIds.size === 0) return false
      const side = this.requireSide
      const map = new Map((this.transactions || []).map(t => [+t.id, t]))
      for (const id of this.selectedIds) {
        const row = map.get(+id)
        if (!row) return false
        if (!this.hasDraft(id, side) && !this.hasFiles(id, side)) return false
      }
      return true
    }
  },
  data () {
    return {
      loading: false,
      drafts: {},
      fileDrafts: {},
      sendingMap: {},
      // showMessageDetail: false,
      // showTransactionDetail: false,
      // selectedTransaction: null,
      // selectedMessage: null,
      baseHeaders: [
        { key: '_select', label: '', tdClass: 'align-middle text-center', thClass: 'text-center', sortable: false },
        { key: 'businessdate', label: 'Date', sortable: true, tdClass: 'align-middle' },
        { key: 'linkedqbaccount', label: 'Source', sortable: true, tdClass: 'align-middle' },
        { key: 'qbtype', label: 'Type', sortable: true, tdClass: 'align-middle' },
        { key: 'qbentity', label: 'Vendor', sortable: true, tdClass: 'align-middle' },
        { key: 'qbclass', label: 'Class', sortable: true, tdClass: 'align-middle' },
        { key: 'qbpayment', label: 'Acccount', sortable: true, tdClass: 'align-middle' },
        { key: 'amount', label: 'Amount', sortable: true, tdClass: 'align-middle text-right', thClass: 'text-right' },
        { key: 'statusRank', label: 'Status', sortable: true, tdClass: 'align-middle' },
        { key: 'question', label: 'Question (Bookkeeper)', tdClass: 'align-top w-25' },
        { key: 'answer', label: 'Answer (Client)', tdClass: 'align-top w-25' }
      ],
      markingMap: {},
      selectedIds: new Set(),
      bulkSending: false
    }
  },
  watch: {
    transactions: {
      immediate: true,
      deep: true,
      handler () {
        this.prepopulateAll()
        const idsOnPage = new Set((this.transactions || []).map(t => +t.id))
        let changed = false
        for (const id of this.selectedIds) {
          if (!idsOnPage.has(+id)) { this.selectedIds.delete(+id); changed = true }
        }
        if (changed) this.selectedIds = new Set(this.selectedIds)
      }
    }
  },
  mounted () {
    this.prepopulateAll()
  },

  methods: {
    placeholderFor (tx) {
      const acct = String(tx.qbaccount || tx.linkedqbaccount || '').toLowerCase()

      if (acct.includes('ask income')) {
        return 'Please explain what type of income this deposit is for...'
      }
      if (acct.includes('ask expense')) {
        return 'Please explain how this expense should be categorized...'
      }
      return 'Enter question here…'
    },
    hasAnyInput (txId, side) {
      return this.hasDraft(txId, side) || this.hasFiles(txId, side)
    },
    fieldState (txId, side) {
      if (!this.isSelected(txId) || side !== this.requireSide) return null
      return this.hasAnyInput(txId, side)
    },
    isSelected (id) { return this.selectedIds.has(+id) },
    toggleSelect (id, checked) {
      const key = +id
      if (checked) this.selectedIds.add(key)
      else this.selectedIds.delete(key)
      this.selectedIds = new Set(this.selectedIds)
    },
    toggleSelectAll (checked) {
      if (checked) (this.transactions || []).forEach(t => this.selectedIds.add(+t.id))
      else (this.transactions || []).forEach(t => this.selectedIds.delete(+t.id))
      this.selectedIds = new Set(this.selectedIds)
    },
    clearSelection () {
      this.selectedIds.clear()
      this.selectedIds = new Set()
    },
    needsPrepopulate (tx) {
      if (!this.canAsk) return false
      const hasBK = this.hasMsgByRole(tx, ['Bookkeeper', 'Administrator'])
      return !hasBK
    },
    prepopulateFor (tx) {
      if (!this.needsPrepopulate(tx)) return
      const already = (this.drafts[tx.id]?.question || '').trim()
      if (already) return
      const seed = (tx.memo && String(tx.memo).trim())
        ? String(tx.memo).trim()
        : false
      if (seed && seed !== 'Deposit') {
        this.ensureDraftSlot(tx.id)
        this.$set(this.drafts[tx.id], 'question', seed)
      }
    },
    prepopulateAll () {
      (this.transactions || []).forEach(this.prepopulateFor)
    },
    ensureDraftSlot (txId) {
      if (!this.drafts[txId]) this.$set(this.drafts, txId, { question: '', answer: '' })
    },
    setDraft (txId, side, val) {
      this.ensureDraftSlot(txId)
      this.$set(this.drafts[txId], side, val)
    },
    draftOf (txId, side) {
      return this.drafts[txId]?.[side] ?? ''
    },
    hasDraft (txId, side) {
      const v = (this.drafts[txId]?.[side] || '').trim()
      return v.length > 0
    },
    resetDraft (txId, side) {
      this.ensureDraftSlot(txId)
      this.$set(this.drafts[txId], side, '')
    },
    getKey (txId, side) {
      return `${txId}:${side}`
    },
    lastMsgForSide (tx, side) {
      const roles = side === 'question'
        ? ['Bookkeeper', 'Administrator']
        : ['Client']
      return this.lastMsgByRole(tx, roles)
    },
    docsForSide (tx, side) {
      const msg = this.lastMsgByRole(
        tx,
        side === 'question' ? ['Bookkeeper', 'Administrator'] : ['Client']
      )
      return Array.isArray(msg?.documents) ? msg.documents : []
    },
    senderStamp (tx, side) {
      const m = this.lastMsgForSide(tx, side)
      if (!m) return null
      const name = m.sender || m.email || 'Unknown'
      const when = this.formatStamp(m.created)
      return `Sent by ${name}, ${when}`
    },
    // formatStamp (dt) {
    //   if (!dt) return ''
    //   const utc = new Date((dt || '').replace(' ', 'T') + 'Z')
    //   if (isNaN(utc.getTime())) return dt

    //   const options = {
    //     timeZone: 'America/Phoenix',
    //     month: '2-digit',
    //     day: '2-digit',
    //     year: 'numeric',
    //     hour: '2-digit',
    //     minute: '2-digit',
    //     hour12: true
    //   }
    //   const formatted = utc.toLocaleString('en-US', options)
    //   return formatted.replace(',', '')
    // },
    formatStamp (dt) {
      if (!dt) return ''
      const m = String(dt).match(
        /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/
      )
      if (!m) return `${dt} Arizona Time`

      const [, y, mo, d, hh, mm] = m
      let hour = parseInt(hh, 10)
      const ampm = hour >= 12 ? 'PM' : 'AM'
      hour = hour % 12
      if (hour === 0) hour = 12

      const pretty = `${mo}/${d}/${y} ${String(hour).padStart(2, '0')}:${mm} ${ampm}`
      return `${pretty} Arizona Time`
    },
    draftText (tx, side) {
      const existing = this.drafts[tx.id]?.[side]
      if (existing !== undefined && existing !== null && existing !== '') {
        return existing
      }
      const roleLabel = side === 'question' ? ['Bookkeeper', 'Administrator'] : ['Client']
      const last = this.lastMsgByRole(tx, roleLabel)
      return last ? this.htmlToText(last.content || '') : ''
    },

    htmlToText (html) {
      try {
        const parser = new DOMParser()
        const doc = parser.parseFromString(html || '', 'text/html')
        doc.querySelectorAll('br').forEach(br => (br.outerHTML = '\n'))
        const blockTags = ['p', 'div', 'li', 'ul', 'ol', 'table', 'tr', 'thead', 'tbody']
        blockTags.forEach(tag => {
          doc.querySelectorAll(tag).forEach(el => {
            if (!el.textContent.endsWith('\n')) el.append('\n')
          })
        })
        return doc.body.textContent.replace(/\n{3,}/g, '\n\n').trim()
      } catch (e) {
        return String(html || '')
          .replace(/<br\s*\/?>/gi, '\n')
          .replace(/<\/p>/gi, '\n')
          .replace(/<[^>]+>/g, '')
          .replace(/&nbsp;/g, ' ')
          .replace(/&amp;/g, '&')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/\n{3,}/g, '\n\n')
          .trim()
      }
    },
    sortCompare (a, b, key) {
      if (key === 'statusRank') {
        if (a.statusRank !== b.statusRank) return a.statusRank - b.statusRank
        const da = a.businessdate ? new Date(a.businessdate).getTime() : 0
        const db = b.businessdate ? new Date(b.businessdate).getTime() : 0
        return db - da
      }
      return null
    },
    hasMsgByRole (tx, roles) {
      const msgs = Array.isArray(tx.messages) ? tx.messages : []
      return msgs.some(m => roles.includes(m.senderrole))
    },

    getStatus (tx) {
      const unread = Number(tx.messagesunread || 0)
      const hasBook = this.hasMsgByRole(tx, ['Bookkeeper', 'Administrator'])
      const hasClient = this.hasMsgByRole(tx, ['Client'])
      const hasAny = hasBook || hasClient

      if (this.canAsk) {
        // BOOKKEEPER / ADMIN view
        if (unread > 0) return 'Client Responded'
        if (!hasAny) return 'Question not sent'
        if (hasBook && !hasClient) return 'Awaiting Feedback'
        // has both sides, and nothing unread -> complete
        return 'Marked Complete'
      } else {
        // CLIENT view
        if ((unread > 0) || (hasBook && !hasClient)) return 'Awaiting Feedback'
        if (!hasAny) return 'No action required'
        if (hasClient) return 'Client Responded'
        return 'No action required'
      }
    },

    showAction (tx) {
      return this.canAsk && this.getStatus(tx) === 'Client Responded'
    },
    lastMsgByRole (tx, roleLabel) {
      const msgs = Array.isArray(tx.messages) ? tx.messages : []
      const sorted = [...msgs].sort((a, b) => {
        const da = a?.created ? new Date(a.created.replace(' ', 'T')) : 0
        const db = b?.created ? new Date(b.created.replace(' ', 'T')) : 0
        if (db - da !== 0) return db - da
        return (+b.id || 0) - (+a.id || 0)
      })
      return sorted.find(m => roleLabel.includes(m.senderrole)) || null
    },
    // openMessageView (message) {
    //   if (!message) return
    //   this.selectedMessage = message
    //   this.showMessageDetail = true
    // },
    // openDetail (t) {
    //   const tx = { ...t }
    //   if (typeof tx.data === 'string') {
    //     try { tx.data = JSON.parse(tx.data) } catch (_) {}
    //   }
    //   tx.account = { name: tx.plaidaccount, type: tx.plaidaccounttype }
    //   this.selectedTransaction = tx
    //   this.showTransactionDetail = true
    // },
    fileRefId (txId, side) {
      return `file-${txId}-${side}`
    },
    triggerFilePicker (txId, side) {
      const ref = this.$refs[this.fileRefId(txId, side)]
      const el = Array.isArray(ref) ? ref[0] : ref
      if (el && el.click) el.click()
    },
    onNativeFilesPicked (txId, side, e) {
      const files = Array.from(e?.target?.files || [])
      this.setFiles(txId, side, files)
      e.target.value = ''
    },
    ensureFileSlot (txId) {
      if (!this.fileDrafts[txId]) this.$set(this.fileDrafts, txId, { question: [], answer: [] })
    },
    setFiles (txId, side, files) {
      this.ensureFileSlot(txId)
      this.$set(this.fileDrafts[txId], side, Array.isArray(files) ? files : (files ? [files] : []))
    },
    filesOf (txId, side) {
      return this.fileDrafts[txId]?.[side] || []
    },
    hasFiles (txId, side) {
      return (this.fileDrafts[txId]?.[side] || []).length > 0
    },
    resetFiles (txId, side) {
      this.ensureFileSlot(txId)
      this.$set(this.fileDrafts[txId], side, [])
    },
    async markComplete (tx) {
      const latestClientMsg = this.lastMsgByRole(tx, ['Client'])
      if (!latestClientMsg || !latestClientMsg.id) {
        this.$bvToast?.toast('No client response to mark complete.', {
          title: 'Nothing to mark',
          variant: 'warning',
          solid: true
        })
        return
      }
      this.$set(this.markingMap, tx.id, true)
      try {
        await MessageService.get(+latestClientMsg.id)
        const fresp = await FeedbackService.getById({ company_id: tx.company_id, id: tx.id })
        const freshTx = fresp?.data?.info?.transaction
        if (freshTx) this.$emit('update', freshTx)

        this.$bvToast?.toast('Marked complete.', {
          title: 'Success',
          variant: 'success',
          solid: true
        })
      } catch (e) {
        console.error(e)
        this.$bvToast?.toast('Could not mark complete.', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.$set(this.markingMap, tx.id, false)
      }
    },
    effectiveContent (tx, side) {
      const draft = (this.drafts[tx.id]?.[side] || '').trim()
      if (draft) return draft

      const fromText = (this.draftText(tx, side) || '').trim()
      if (fromText) return fromText
      return ''
    },
    async sendAll () {
      if (!this.canBulkSend || this.bulkSending) return
      this.bulkSending = true
      const side = this.requireSide

      try {
        const form = new FormData()
        const msgs = []

        const orderMap = new Map((this.transactions || []).map((t, i) => [+t.id, i]))
        const selectedSorted = [...this.selectedIds].sort((a, b) => (orderMap.get(+a) ?? 0) - (orderMap.get(+b) ?? 0))
        const txMap = new Map((this.transactions || []).map(t => [+t.id, t]))
        selectedSorted.forEach((id, idx) => {
          const tx = txMap.get(+id)
          if (!tx) return

          const content = this.effectiveContent(tx, side)
          const files = this.fileDrafts[id]?.[side] || []

          msgs.push({
            qbtransaction_id: +id,
            content,
            from: 'feedback'
          })

          files.forEach(f => {
            form.append(`files_${idx}[]`, f, f.name)
          })
        })

        form.append('messages', JSON.stringify(msgs))

        await MessageService.bulkSend(form)

        const ids = selectedSorted.map(Number)
        const any = this.transactions?.[0]
        const companyId = any ? any.company_id : null
        if (companyId) {
          const fresp = await FeedbackService.getMany({ company_id: companyId, ids })
          const fresh = fresp?.data?.info?.transactions || fresp?.data?.result?.transactions || fresp?.data?.transactions || []
          // emit updates so parent keeps sort/rank behavior
          fresh.forEach(tx => this.$emit('update', tx))
        }

        // Clear drafts/files for those ids
        ids.forEach(id => {
          this.resetDraft(id, side)
          this.resetFiles(id, side)
        })

        this.$bvToast?.toast('All messages sent.', { title: 'Success', variant: 'success', solid: true })
        this.clearSelection()
      } catch (e) {
        console.error(e)
        this.$bvToast?.toast('Bulk send failed for one or more items.', { title: 'Error', variant: 'danger', solid: true })
      } finally {
        this.bulkSending = false
      }
    },
    async send (tx, side) {
      const key = this.getKey(tx.id, side)
      const contentRaw = this.effectiveContent(tx, side)
      const files = this.filesOf(tx.id, side)
      if (!contentRaw && files.length === 0) return

      this.$set(this.sendingMap, key, true)
      try {
        const formData = new FormData()
        formData.append('content', contentRaw || '')
        formData.append('qbtransaction_id', tx.id)
        formData.append('from', 'feedback')
        files.forEach((f) => {
          formData.append('files[]', f, f.name)
        })

        await MessageService.send(formData)

        const fresp = await FeedbackService.getById({ company_id: tx.company_id, id: tx.id })
        const freshTx = fresp?.data?.info?.transaction
        if (freshTx) {
          this.$emit('update', freshTx)
        }

        this.resetDraft(tx.id, side)
        this.resetFiles(tx.id, side)
      } catch (e) {
        this.$bvToast?.toast('Failed to send message', { title: 'Error', variant: 'danger', solid: true })
        console.error(e)
      } finally {
        this.$set(this.sendingMap, key, false)
      }
    }
    // handleMessageUpdated (msg) {
    //   const tx = this.transactions.find(t => +t.id === +(msg.qbtransaction_id || msg.plaidtransaction_id))
    //   if (!tx) return

    //   if (!Array.isArray(tx.messages)) this.$set(tx, 'messages', [])
    //   const idx = tx.messages.findIndex(m => +m.id === +msg.id)
    //   if (idx >= 0) {
    //     this.$set(tx.messages, idx, msg)
    //   } else {
    //     this.$set(tx, 'messages', [...tx.messages, msg])
    //   }

    //   // If user hasn't typed anything for that side, clear draft to reveal latest message
    //   const side = (['Bookkeeper', 'Administrator'].includes(msg.senderrole)) ? 'question' : (msg.senderrole === 'Client') ? 'answer' : null
    //   if (side) {
    //     const current = this.drafts[tx.id]?.[side]
    //     if (!current) this.resetDraft(tx.id, side)
    //   }

    //   this.$emit('update', msg)
    //   this.$nextTick(() => this.$root.$emit('bv::refresh::table', this.tableName))
    // }
  }
}
</script>
